<template>
    <div class="customer-orders-container">
        <div v-if="customer" class="modern-orders-card">
            <div class="orders-header">
                <div class="header-info">
                    <h3 class="customer-name">{{ customer.customer_name }}</h3>
                    <div class="customer-contact">
                        <!-- Phone (Primary) -->
                        <span v-if="customer.customer_phone" class="contact-item phone-item">  
                            <b-icon icon="telephone" class="mr-1"></b-icon>
                            {{ formatPhone(customer.customer_phone) }}
                            <span class="badge badge-light ml-2">Primary</span>
                        </span>
                        <!-- Email -->
                        <span v-if="customer.customer_email" class="contact-item">
                            <b-icon icon="envelope" class="mr-1"></b-icon>
                            {{ customer.customer_email }}
                        </span>
                        <!-- Customer ID -->
                        <span v-if="customer.customer_primary_id" class="contact-item">
                            <b-icon icon="fingerprint" class="mr-1"></b-icon>
                            <code class="customer-id">{{ customer.customer_primary_id }}</code>
                        </span>
                    </div>
                </div>
                
                <div class="header-stats">
                    <div class="stat-item">
                        <div class="stat-value">{{ orders.length }}</div>
                        <div class="stat-label">Total Orders</div>
                    </div>
                    <div class="stat-item">
                        <div class="stat-value">{{ formatCurrency(totalSpent) }}</div>
                        <div class="stat-label">Total Spent</div>
                    </div>
                    <div class="stat-item">
                        <div class="stat-value">{{ formatCurrency(avgOrderValue) }}</div>
                        <div class="stat-label">Avg Order</div>
                    </div>
                </div>
            </div>

            <!-- Customer Info Summary -->
            <div class="customer-summary-card mb-4">
                <b-card body-class="p-3">
                    <b-row>
                        <b-col md="4">
                            <div class="info-item">
                                <strong>Customer ID:</strong>
                                <span class="ml-2">{{ customer.customer_primary_id || 'Not set' }}</span>
                            </div>
                        </b-col>
                        <b-col md="4">
                            <div class="info-item">
                                <strong>Phone (Primary):</strong>
                                <span class="ml-2">{{ customer.customer_phone ? formatPhone(customer.customer_phone) : 'Not set' }}</span>
                            </div>
                        </b-col>
                        <b-col md="4">
                            <div class="info-item">
                                <strong>Email:</strong>
                                <span class="ml-2">{{ customer.customer_email || 'Not set' }}</span>
                            </div>
                        </b-col>
                    </b-row>
                </b-card>
            </div>

            <!-- Order Filters -->
            <div class="filters-section mb-4">
                <b-card class="filters-card">
                    <b-row>
                        <b-col md="4">
                            <b-form-group label="Date Range" label-for="date-range">
                                <date-picker
                                    v-model="date_range"
                                    format="MM-DD-YYYY"
                                    valueType="format"
                                    style="width: 100%;"
                                    range
                                    placeholder="Filter by date range"
                                ></date-picker>
                            </b-form-group>
                        </b-col>
                        
                        <b-col md="3">
                            <b-form-group label="Order Type" label-for="order-type">
                                <b-form-select
                                    id="order-type"
                                    v-model="order_type_filter"
                                    :options="order_type_options"
                                >
                                    <option :value="null">All Types</option>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                        
                        <b-col md="3">
                            <b-form-group label="Status" label-for="status">
                                <b-form-select
                                    id="status"
                                    v-model="status_filter"
                                    :options="status_options"
                                >
                                    <option :value="null">All Status</option>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                        
                        <b-col md="2" class="d-flex align-items-end">
                            <b-button @click="applyOrderFilters" variant="primary" block>
                                <b-icon icon="filter" class="mr-1"></b-icon>
                                Filter
                            </b-button>
                        </b-col>
                    </b-row>
                </b-card>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="loading-container">
                <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
                <div class="loading-text">Loading orders...</div>
            </div>

            <!-- Orders Table -->
            <div v-else-if="filteredOrders.length > 0" class="orders-table-container">
                <div class="table-responsive">
                    <table class="modern-table">
                        <thead>
                            <tr>
                                <th class="table-header">INVOICE #</th>
                                <th class="table-header">DATE</th>
                                <th class="table-header">LOCATION</th>
                                <th class="table-header">SALES PERSON</th>
                                <th class="table-header">STATUS</th>
                                <th class="table-header">TYPE</th>
                                <th class="table-header">ITEMS</th>
                                <th class="table-header">TOTAL</th>
                                <th class="table-header">PAYMENT</th>
                                <th class="table-header">ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(order, idx) in filteredOrders" :key="idx" class="table-row">
                                <td class="invoice-cell">
                                    <strong>{{ order.invoice_id }}</strong>
                                </td>
                                
                                <td class="date-cell">
                                    {{ formatDate(order.ts) }}
                                </td>
                                
                                <td class="location-cell">
                                    {{ getLocationName(order.location_id) }}
                                </td>
                                
                                <td class="salesperson-cell">
                                    {{ order.username || 'N/A' }}
                                </td>
                                
                                <td class="status-cell">
                                    <span class="status-badge" :class="getStatusClass(order)">
                                        {{ getStatusText(order) }}
                                    </span>
                                </td>
                                
                                <td class="type-cell">
                                    <span class="type-badge" :class="getTypeClass(order)">
                                        {{ getTypeText(order) }}
                                    </span>
                                </td>
                                
                                <td class="items-cell">
                                    {{ order.total_items || 0 }}
                                </td>
                                
                                <td class="total-cell">
                                    {{ formatCurrency(order.total) }}
                                </td>
                                
                                <td class="payment-cell">
                                    <span v-if="order.is_paid" class="badge badge-success">Paid</span>
                                    <span v-else class="badge badge-warning">Pending</span>
                                </td>
                                
                                <td class="actions-cell">
                                    <div class="action-buttons-wrapper">
                                        <b-button 
                                            @click="viewOrderDetails(order)" 
                                            variant="outline-primary" 
                                            size="sm"
                                            class="action-btn"
                                            title="View Details"
                                        >
                                            <b-icon icon="eye"></b-icon>
                                        </b-button>
                                        
                                        <b-button 
                                            v-if="canEditOrder(order)"
                                            @click="editOrder(order)" 
                                            variant="outline-info" 
                                            size="sm"
                                            class="action-btn"
                                            title="Edit Order"
                                        >
                                            <b-icon icon="pencil"></b-icon>
                                        </b-button>
                                        
                                        <b-button 
                                            @click="printInvoice(order)" 
                                            variant="outline-secondary" 
                                            size="sm"
                                            class="action-btn"
                                            title="Print Invoice"
                                        >
                                            <b-icon icon="printer"></b-icon>
                                        </b-button>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                
                <!-- Order Summary -->
                <div class="order-summary mt-4">
                    <b-card class="summary-card">
                        <b-row>
                            <b-col md="3">
                                <div class="summary-item">
                                    <div class="summary-label">Total Orders</div>
                                    <div class="summary-value">{{ filteredOrders.length }}</div>
                                </div>
                            </b-col>
                            <b-col md="3">
                                <div class="summary-item">
                                    <div class="summary-label">Total Revenue</div>
                                    <div class="summary-value">{{ formatCurrency(filteredTotal) }}</div>
                                </div>
                            </b-col>
                            <b-col md="3">
                                <div class="summary-item">
                                    <div class="summary-label">Avg Order Value</div>
                                    <div class="summary-value">{{ formatCurrency(filteredAvg) }}</div>
                                </div>
                            </b-col>
                            <b-col md="3">
                                <div class="summary-item">
                                    <div class="summary-label">Pending Payments</div>
                                    <div class="summary-value">{{ pendingOrders }}</div>
                                </div>
                            </b-col>
                        </b-row>
                    </b-card>
                </div>
            </div>
            
            <!-- Empty State -->
            <div v-else class="empty-state">
                <div class="empty-state-icon">
                    <b-icon icon="receipt" font-scale="3"></b-icon>
                </div>
                <h3>No Orders Found</h3>
                <p>No orders found for this customer{{ hasFilters ? ' with current filters' : '' }}</p>
                <div class="empty-state-actions mt-3">
                    <b-button @click="clearFilters" variant="outline-secondary" v-if="hasFilters">
                        Clear Filters
                    </b-button>
                </div>
            </div>
        </div>
        
        <div v-else class="text-center text-muted p-4">
            <p>No customer data available.</p>
        </div>

        <!-- ORDER DETAILS MODAL -->
        <b-modal 
            v-model="showOrderModal"
            id="order-details-modal" 
            size="xl" 
            :title="selectedOrder ? 'Order #' + selectedOrder.invoice_id : 'Order Details'"
            hide-footer
            @hidden="closeOrderModal"
            scrollable
            centered
        >
            <div v-if="selectedOrder">
                <order-details 
                    :order="selectedOrder" 
                    :key="selectedOrder.invoice_id"
                />
            </div>
            <div v-else class="text-center p-4">
                <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
                <p class="mt-2">Loading order details...</p>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import Requestor from '@/utilities/Requestor'
    import DatePicker from 'vue2-datepicker'
    import 'vue2-datepicker/index.css'
    import OrderDetails from './OrderDetails.vue'

    export default {
        name: "CustomerOrders",
        components: { 
            DatePicker,
            OrderDetails
        },
        props: ["customer"],
        data: function() {
            return {
                packet: {},
                orders: [],
                filteredOrders: [],
                loading: false,
                date_range: [],
                order_type_filter: null,
                status_filter: null,
                order_type_options: [
                    { value: 'regular', text: 'Regular Sale' },
                    { value: 'layaway', text: 'Layaway' },
                    { value: 'return', text: 'Return' },
                    { value: 'exchange', text: 'Exchange' }
                ],
                status_options: [
                    { value: 'pending', text: 'Pending' },
                    { value: 'completed', text: 'Completed' },
                    { value: 'cancelled', text: 'Cancelled' },
                    { value: 'dispatched', text: 'Dispatched' }
                ],
                showOrderModal: false,
                selectedOrder: null
            };
        },
        computed: {
            totalSpent() {
                return this.orders.reduce((sum, order) => sum + (parseFloat(order.total) || 0), 0);
            },
            
            avgOrderValue() {
                if (this.orders.length === 0) return 0;
                return this.totalSpent / this.orders.length;
            },
            
            filteredTotal() {
                return this.filteredOrders.reduce((sum, order) => sum + (parseFloat(order.total) || 0), 0);
            },
            
            filteredAvg() {
                if (this.filteredOrders.length === 0) return 0;
                return this.filteredTotal / this.filteredOrders.length;
            },
            
            pendingOrders() {
                return this.filteredOrders.filter(order => !order.is_paid).length;
            },
            
            hasFilters() {
                return this.date_range.length > 0 || 
                       this.order_type_filter !== null || 
                       this.status_filter !== null;
            }
        },
        methods: {
            loadCustomerOrders() {
                if (!this.customer) {
                    console.error('Customer is missing:', this.customer);
                    return;
                }
                
                this.loading = true;
                var that = this;
                var path = "/api/customers/get_customer_orders/";
                
                // UPDATED: Send both phone and email in the payload
                var data = {
                    customer_phone: this.customer.customer_phone || null,
                    customer_email: this.customer.customer_email || null
                };
                
                console.log('Loading customer orders with payload:', data);
                
                var success = function(response) {
                    console.log('Orders response:', response);
                    if (response.data.error) {
                        that.$bvToast.toast(
                            response.data.error,
                            { title: "Error", autoHideDelay: 1500, variant: "warning" }
                        );
                    } else {
                        that.orders = response.data.result || [];
                        that.filteredOrders = response.data.result || [];
                        console.log('Orders loaded:', that.orders.length);
                    }
                    that.loading = false;
                };
                
                var failure = function(error) {
                    console.error('Error loading orders:', error);
                    that.$bvToast.toast(
                        "Error loading orders. Please try again.",
                        { title: "Error", autoHideDelay: 1500, variant: "warning" }
                    );
                    that.loading = false;
                };
                
                Requestor.postSyncP(path, data, this, success, failure);
            },
            
            applyOrderFilters() {
                var filtered = this.orders;

                if (this.date_range && this.date_range.length === 2) {
                    filtered = filtered.filter(function(order) {
                        var orderDate = new Date(order.ts);
                        var startDate = new Date(this.date_range[0]);
                        var endDate = new Date(this.date_range[1]);
                        return orderDate >= startDate && orderDate <= endDate;
                    }.bind(this));
                }

                if (this.order_type_filter) {
                    if (this.order_type_filter === 'layaway') {
                        filtered = filtered.filter(function(order) {
                            return order.is_layaway || order.was_layaway;
                        });
                    } else if (this.order_type_filter === 'regular') {
                        filtered = filtered.filter(function(order) {
                            return !order.is_layaway && !order.was_layaway;
                        });
                    }
                }

                if (this.status_filter) {
                    filtered = filtered.filter(function(order) {
                        if (this.status_filter === 'pending') {
                            return !order.completed;
                        } else if (this.status_filter === 'completed') {
                            return order.completed;
                        } else if (this.status_filter === 'dispatched') {
                            return order.dispatched_at !== null;
                        }
                        return true;
                    }.bind(this));
                }

                this.filteredOrders = filtered;
            },
            
            clearFilters() {
                this.date_range = [];
                this.order_type_filter = null;
                this.status_filter = null;
                this.filteredOrders = this.orders;
            },
            
            formatCurrency(value) {
                if (!value && value !== 0) return '$0.00';
                const num = parseFloat(value);
                if (isNaN(num)) return '$0.00';
                return '$' + num.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            },
            
            formatDate(dateString) {
                if (!dateString) return 'N/A';
                try {
                    var date = new Date(dateString);
                    return date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    });
                } catch (error) {
                    return dateString;
                }
            },
            
            formatPhone(phone) {
                if (!phone) return '';
                var cleaned = ('' + phone).replace(/\D/g, '');
                var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
                if (match) {
                    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
                }
                return phone;
            },
            
            getLocationName(locationId) {
                if (!locationId || !this.packet.locationsdict) return 'N/A';
                return this.packet.locationsdict[locationId] || 'N/A';
            },
            
            getStatusClass(order) {
                if (order.completed) return 'status-completed';
                if (order.dispatched_at) return 'status-dispatched';
                return 'status-pending';
            },
            
            getStatusText(order) {
                if (order.completed) return 'Completed';
                if (order.dispatched_at) return 'Dispatched';
                return 'Pending';
            },
            
            getTypeClass(order) {
                if (order.is_layaway) return 'layaway';
                if (order.is_return) return 'return';
                if (order.is_exchange) return 'exchange';
                return 'sale';
            },
            
            getTypeText(order) {
                if (order.is_layaway) return 'Layaway';
                if (order.is_return) return 'Return';
                if (order.is_exchange) return 'Exchange';
                return 'Sale';
            },
            
            canEditOrder(order) {
                if (!this.packet || !this.packet.userDetails) {
                    return false;
                }
                
                const userRole = this.packet.userDetails.userrole;
                const allowedRoles = ['admin', 'salesperson', 'locationadmin'];
                return allowedRoles.includes(userRole) && !order.completed;
            },
            
            viewOrderDetails(order) {
                console.log('View order details clicked for:', order.invoice_id);
                console.log('Order data:', JSON.stringify(order, null, 2));
                
                this.selectedOrder = order;
                this.showOrderModal = true;
                
                // Trigger modal show
                this.$nextTick(() => {
                    this.$bvModal.show('order-details-modal');
                });
            },
            
            closeOrderModal() {
                this.showOrderModal = false;
                this.selectedOrder = null;
            },
            
            editOrder(order) {
                this.$router.push(`/sales/edit/${order.invoice_id}`);
            },
            
            printInvoice(order) {
                window.open(`/api/invoice/print/${order.invoice_id}`, '_blank');
            }
        },
        watch: {
            customer: {
                immediate: true,
                handler(newVal) {
                    if (newVal) {
                        console.log('Customer changed, loading orders:', newVal);
                        this.loadCustomerOrders();
                    }
                }
            }
        },
        created: function() {
            this.packet = JSON.parse(localStorage.getItem('packet')) || {};
            console.log('CustomerOrders created with customer:', this.customer);
        }
    };
</script>

<style scoped>
.customer-orders-container {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
}

.modern-orders-card {
    background: white;
    border-radius: 10px;
    padding: 0;
}

.orders-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 25px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 10px 10px 0 0;
}

.header-info {
    flex: 1;
}

.customer-name {
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 10px;
}

.customer-contact {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
}

.contact-item {
    display: flex;
    align-items: center;
    font-size: 0.95rem;
    opacity: 0.9;
    background: rgba(255, 255, 255, 0.1);
    padding: 6px 12px;
    border-radius: 6px;
}

.contact-item.phone-item {
    background: rgba(255, 255, 255, 0.2);
    font-weight: 600;
}

.phone-item .badge {
    font-size: 0.7rem;
    padding: 2px 6px;
}

.customer-id {
    background: rgba(255, 255, 255, 0.1);
    padding: 2px 6px;
    border-radius: 4px;
    font-family: monospace;
    font-size: 0.9rem;
}

.header-stats {
    display: flex;
    gap: 20px;
}

.stat-item {
    text-align: center;
    padding: 15px;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    min-width: 100px;
}

.stat-value {
    font-size: 1.2rem;
    font-weight: 700;
    margin-bottom: 5px;
}

.stat-label {
    font-size: 0.8rem;
    opacity: 0.8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Customer Summary */
.customer-summary-card {
    padding: 0 25px;
}

.customer-summary-card .card {
    border: none;
    background: #f8f9fa;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.info-item {
    padding: 8px 0;
    border-bottom: 1px solid #e2e8f0;
}

.info-item:last-child {
    border-bottom: none;
}

.filters-section {
    padding: 0 25px;
}

.filters-card {
    border: none;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.loading-container {
    text-align: center;
    padding: 40px 20px;
}

.loading-text {
    margin-top: 15px;
    color: #4a5568;
}

.orders-table-container {
    padding: 0 25px 25px;
}

.modern-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
}

.table-header {
    padding: 12px 15px;
    text-align: left;
    font-weight: 600;
    color: #4a5568;
    background: #f8fafc;
    border-bottom: 2px solid #e2e8f0;
    white-space: nowrap;
}

.table-row {
    transition: background 0.2s ease;
}

.table-row:hover {
    background: #f8fafc;
}

.table-row td {
    padding: 10px 15px;
    border-bottom: 1px solid #e2e8f0;
    vertical-align: middle;
}

.status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
}

.status-completed {
    background: rgba(72, 187, 120, 0.15);
    color: #276749;
    border: 1px solid rgba(72, 187, 120, 0.3);
}

.status-dispatched {
    background: rgba(66, 153, 225, 0.15);
    color: #2c5282;
    border: 1px solid rgba(66, 153, 225, 0.3);
}

.status-pending {
    background: rgba(237, 137, 54, 0.15);
    color: #c05621;
    border: 1px solid rgba(237, 137, 54, 0.3);
}

.type-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
}

.type-badge.layaway {
    background: rgba(159, 122, 234, 0.15);
    color: #553c9a;
    border: 1px solid rgba(159, 122, 234, 0.3);
}

.type-badge.return, .type-badge.exchange {
    background: rgba(245, 101, 101, 0.15);
    color: #c53030;
    border: 1px solid rgba(245, 101, 101, 0.3);
}

.type-badge.sale {
    background: rgba(66, 153, 225, 0.15);
    color: #2c5282;
    border: 1px solid rgba(66, 153, 225, 0.3);
}

.action-buttons-wrapper {
    display: flex;
    gap: 5px;
    align-items: center;
}

.action-btn {
    padding: 6px;
    font-size: 0.85rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    transition: all 0.2s ease;
    width: 32px;
    height: 32px;
}

.order-summary {
    padding: 0 25px;
}

.summary-card {
    border: none;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    background: #f8fafc;
}

.summary-item {
    text-align: center;
    padding: 15px;
}

.summary-label {
    font-size: 0.9rem;
    color: #718096;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 5px;
}

.summary-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2d3748;
}

.empty-state {
    text-align: center;
    padding: 60px 20px;
    border-radius: 8px;
    background: #f8fafc;
    border: 1px dashed #e2e8f0;
    margin: 25px;
}

.empty-state-icon {
    color: #a0aec0;
    margin-bottom: 15px;
}

.empty-state h3 {
    color: #2d3748;
    margin-bottom: 8px;
    font-weight: 600;
}

.empty-state p {
    color: #718096;
    margin-bottom: 15px;
}

.empty-state-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
}

@media (max-width: 1200px) {
    .orders-header {
        flex-direction: column;
        gap: 20px;
    }
    
    .header-stats {
        width: 100%;
        justify-content: space-between;
    }
}

@media (max-width: 768px) {
    .orders-header {
        padding: 20px;
    }
    
    .orders-table-container {
        padding: 0 15px 15px;
    }
    
    .filters-section {
        padding: 0 15px;
    }
    
    .table-responsive {
        overflow-x: auto;
        font-size: 0.8rem;
    }
    
    .table-header, .table-row td {
        padding: 8px 10px;
    }
    
    .header-stats {
        flex-wrap: wrap;
        gap: 10px;
    }
    
    .stat-item {
        min-width: calc(50% - 5px);
    }
    
    .customer-contact {
        flex-direction: column;
        align-items: flex-start;
    }
    
    .contact-item {
        width: 100%;
    }
}

@media (max-width: 480px) {
    .customer-name {
        font-size: 1.5rem;
    }
    
    .filters-card .row {
        flex-direction: column;
        gap: 15px;
    }
    
    .action-buttons-wrapper {
        flex-direction: column;
        gap: 5px;
    }
    
    .action-btn {
        width: 28px;
        height: 28px;
    }
    
    .empty-state-actions {
        flex-direction: column;
        align-items: center;
    }
    
    .customer-summary-card .row {
        flex-direction: column;
        gap: 10px;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/Customers/CustomerOrders.vue